import type React from 'react'
import { useCallback, useMemo } from 'react'

import { useNavigate, useParams } from '@tanstack/react-router'

import { AdminPromotionView } from '@/components/templates/AdminPromotionView/AdminPromotionView.tsx'
import { useBackToPilote } from '@/pages/actions/useBackToPilote'
import { useAdminPromotionList } from '@/state/promotions/useAdminPromotionList'

export const AdminPromotionPage: React.FC = () => {
  const params = useParams({ from: '/promotions/$initiativeId/national' })
  const promotions = useAdminPromotionList(params)

  const nationalPromotions = useMemo(
    () => promotions.data.filter((promotion) => promotion.type === 'national'),
    [promotions]
  )
  const fallbackPromotions = useMemo(
    () => promotions.data.filter((promotion) => promotion.type === 'fallback'),
    [promotions]
  )

  const navigate = useNavigate()
  const addPromotion = useCallback(
    async (type: 'national' | 'fallback') => {
      await navigate({
        from: '/promotions/$initiativeId/national',
        to: '/promotions/$initiativeId/national/create/$type',
        params: { type },
      })
    },
    [navigate]
  )
  const addPartnerPromotion = useCallback(() => addPromotion('national'), [addPromotion])
  const addDefaultPromotion = useCallback(() => addPromotion('fallback'), [addPromotion])

  const openEditSheet = useCallback(
    async (promotionId: string) =>
      navigate({
        from: '/promotions/$initiativeId/national',
        to: '/promotions/$initiativeId/national/$promotionId',
        params: { promotionId },
        mask: { to: '/promotions/$initiativeId/national', params: { initiativeId: params.initiativeId } },
      }),
    [navigate, params.initiativeId]
  )

  const onBackToPiloteClicked = useBackToPilote()

  return (
    <AdminPromotionView
      nationalPromotions={nationalPromotions}
      fallbackPromotions={fallbackPromotions}
      onAddPartnerPromotion={addPartnerPromotion}
      onAddDefaultPromotion={addDefaultPromotion}
      onBackToPiloteClicked={onBackToPiloteClicked}
      onRowClick={openEditSheet}
    />
  )
}
